import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Bell,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { sd } from '@sd/lib/sdPaths';
import {
  ServiceDeskAPI,
  serviceDeskInAppNotificationsQueryKey,
} from '@sd/services/APIs';
import { toast } from 'sonner';
import { TablePager } from "@sd/components/common/TablePager";

import {
  parseInAppNotifications,
  parseNotificationCount,
  timeAgo,
} from '@sd/lib/inAppNotifications';
import { getNotificationKindMeta } from '@sd/lib/notificationKindMeta';

type AdminInAppNotification = import('@sd/lib/inAppNotifications').InAppNotification;

function parseNotifications(raw: unknown): AdminInAppNotification[] {
  return parseInAppNotifications(raw);
}

function parseCount(raw: unknown, ...keys: string[]): number {
  return parseNotificationCount(raw, ...keys);
}

export function AdminNotifications() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [locallyReadIds, setLocallyReadIds] = useState<Set<string>>(() => new Set());

  const apiParams = useMemo(
    () => ({ page, page_size: pageSize }),
    [page, pageSize]
  );

  const { data: rawData, isLoading, isError } = useQuery({
    queryKey: [...serviceDeskInAppNotificationsQueryKey, apiParams],
    queryFn: () =>
      ServiceDeskAPI.inAppNotifications.list(apiParams).then(r => r.data),
    staleTime: 30_000,
    retry: 1,
  });

  const apiItems = useMemo(() => parseNotifications(rawData), [rawData]);

  const items = useMemo(
    () =>
      apiItems.map(n => ({
        ...n,
        read: n.read || locallyReadIds.has(n.id),
      })),
    [apiItems, locallyReadIds]
  );

  const unreadCount = items.filter(i => !i.read).length;

  const totalCount = parseCount(rawData, 'count', 'total', 'total_count') || items.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const markReadMutation = useMutation({
    mutationFn: (id: string) =>
      ServiceDeskAPI.inAppNotifications.markRead(id).then(r => r.data),
    onMutate: id => {
      setLocallyReadIds(prev => new Set(prev).add(id));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: serviceDeskInAppNotificationsQueryKey,
        exact: false,
      });
    },
    onError: (_err, id) => {
      setLocallyReadIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      toast.error('Failed to mark notification as read');
    },
  });

  const markRead = (id: string) => {
    markReadMutation.mutate(id);
  };

  const markAllReadMutation = useMutation({
    mutationFn: () =>
      ServiceDeskAPI.inAppNotifications.markAllRead().then(r => r.data),
    onMutate: () => {
      setLocallyReadIds(prev => {
        const next = new Set(prev);
        items.filter(i => !i.read).forEach(i => next.add(i.id));
        return next;
      });
    },
    onSuccess: () => {
      toast.success('All marked as read');
      void queryClient.invalidateQueries({
        queryKey: serviceDeskInAppNotificationsQueryKey,
        exact: false,
      });
    },
    onError: () => {
      void queryClient.invalidateQueries({
        queryKey: serviceDeskInAppNotificationsQueryKey,
        exact: false,
      });
      toast.error('Failed to mark all as read');
    },
  });

  const markAllRead = () => {
    if (unreadCount === 0) return;
    markAllReadMutation.mutate();
  };

  return (
    <div className="min-w-0 p-4 sm:p-6">
      <Card className="overflow-hidden border shadow-sm">
        {/* Header — PTS-style */}
        <div className="flex flex-col gap-3 border-b bg-card px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-5">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-foreground">Notifications</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {unreadCount > 0
                ? `You have ${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}`
                : 'You are all caught up'}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full shrink-0 gap-2 sm:w-auto sm:self-auto"
            onClick={markAllRead}
            disabled={unreadCount === 0 || markAllReadMutation.isPending}
          >
            {markAllReadMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4" />
            )}
            Mark All Read
          </Button>
        </div>

        <CardContent className="space-y-3 bg-muted/20 p-3 sm:p-5">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin opacity-60" />
            <p className="text-sm">Loading notifications…</p>
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-muted-foreground">
            <Bell className="h-10 w-10 opacity-40" />
            <p className="font-medium text-foreground">Could not load notifications</p>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-muted-foreground">
            <Bell className="h-10 w-10 opacity-40" />
            <p className="font-medium text-foreground">No notifications</p>
          </div>
        ) : (
          items.map(n => {
            const meta = getNotificationKindMeta(n.kind);
            const Icon = meta.icon;
            const isUnread = !n.read;
            const isMarking =
              markReadMutation.isPending && markReadMutation.variables === n.id;

            return (
              <div
                key={n.id}
                role={isUnread ? 'button' : undefined}
                tabIndex={isUnread ? 0 : undefined}
                onClick={() => {
                  if (isUnread && !isMarking) markRead(n.id);
                }}
                onKeyDown={e => {
                  if (isUnread && (e.key === 'Enter' || e.key === ' ') && !isMarking) {
                    e.preventDefault();
                    markRead(n.id);
                  }
                }}
                className={cn(
                  'relative min-w-0 rounded-lg border border-l-[3px] px-3 py-3 shadow-sm transition-colors sm:px-4 sm:py-4',
                  isUnread
                    ? 'cursor-pointer border-l-blue-600 bg-slate-200/90 hover:bg-slate-300/80 dark:border-l-blue-400 dark:bg-slate-800 dark:hover:bg-slate-700'
                    : 'border-l-muted-foreground/25 bg-card opacity-90 dark:bg-slate-900/50'
                )}
              >
                {isUnread && (
                  <span
                    className="absolute right-3 top-3 h-2.5 w-2.5 rounded-full bg-primary shadow-sm sm:right-4 sm:top-4"
                    title="Unread"
                  />
                )}

                <div className="flex gap-2.5 pr-5 sm:gap-3 sm:pr-6">
                  <div
                    className={cn(
                      'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                      isUnread
                        ? 'bg-slate-300/80 dark:bg-slate-700'
                        : 'bg-muted'
                    )}
                  >
                    <Icon className={cn('h-[18px] w-[18px]', isUnread ? meta.iconClass : 'text-muted-foreground')} />
                  </div>
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <h2
                      className={cn(
                        'text-base leading-snug break-words',
                        isUnread
                          ? 'font-semibold text-slate-900 dark:text-slate-50'
                          : 'font-medium text-foreground/80'
                      )}
                    >
                      {(() => {
                        const subject = n.subject;
                        const id = n.ticketId !== '—' ? n.ticketId : null;
                        return (
                          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            {id && (
                              <Link
                                to={sd('admin/tickets')}
                                onClick={e => e.stopPropagation()}
                                className={cn(
                                  'font-bold text-base tracking-wide hover:underline break-all',
                                  isUnread ? 'text-blue-700 dark:text-blue-400' : 'text-blue-600/70'
                                )}
                              >
                                {id}
                              </Link>
                            )}
                            {subject && (
                              <span className={cn(
                                'font-semibold text-sm break-words',
                                isUnread ? 'text-slate-800 dark:text-slate-200' : 'text-foreground/70'
                              )}>
                                · {subject}
                              </span>
                            )}
                            {!id && !subject && n.title}
                          </span>
                        );
                      })()}
                    </h2>
                    <p
                      className={cn(
                        'text-sm leading-relaxed break-words',
                        isUnread
                          ? 'text-slate-700 dark:text-slate-300'
                          : 'text-muted-foreground'
                      )}
                    >
                      {n.body.replace(/^New task assigned in Service Desk:\s*TXT-\d+\s*[-–]\s*[""].*?[""]\.\s*/i, '')}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pt-0.5 text-xs text-muted-foreground">
                      <span>{timeAgo(n.createdAt)}</span>
                      <span className="text-border">·</span>
                      <Badge
                        variant="secondary"
                        className="h-5 rounded px-2 text-[11px] font-normal text-primary bg-primary/10 hover:bg-primary/10"
                      >
                        {meta.label}
                      </Badge>
                    </div>
                  </div>
                </div>

                {isMarking && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-background/60">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  </div>
                )}
              </div>
            );
          })
        )}
        </CardContent>

        {!isLoading && !isError && totalCount > 0 && (
          <div className="overflow-x-auto">
            <TablePager
              pageNum={page}
              totalPages={totalPages}
              pageSize={pageSize}
              isLoading={isLoading}
              onPageChange={setPage}
              onPageSizeChange={size => { setPageSize(size); setPage(1); }}
            />
          </div>
        )}
      </Card>
    </div>
  );
}
